import React from 'react' // eslint-disable-line no-unused-vars — richiesto dal JSX transform classico di react-scripts 2.x
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Button, Checkbox, Icon, Label, Message, Table } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import '../SuperCutoff.css'

const STATUS_PENDING = 'pending'
const STATUS_RETRIED = 'retried'
const STATUS_RESOLVED = 'resolved'

const NO_PERMISSION_MESSAGE =
  'Errors found during the closure process. Please contact the AEP team to review and retry them.'

/**
 * Restituisce il colore Semantic UI corrispondente allo status di un errore.
 * @param {string} status - Valore dello status dell'errore
 * @returns {string} Nome del colore Semantic UI
 */
const statusColor = (status) => {
  if (status === STATUS_PENDING) return 'red'
  if (status === STATUS_RETRIED) return 'orange'
  if (status === STATUS_RESOLVED) return 'green'
  return 'grey'
}

/**
 * Step 9 — Visualizzazione e retry degli errori di closure.
 *
 * Visibilita' a due livelli (vedi TODO-supercutoff-db-permission-setup.md §13):
 *  - Tutti i Controlling: bottone "Load errors", messaggio "No errors found" se la lista e vuota,
 *    bottone "Proceed to next step" abilitato solo con 0 errori. Se ci sono errori, vedono solo
 *    un alert che invita a contattare il team AEP — niente tabella, niente retry.
 *  - Team AEP (superAdmin via enumerazione, oppure permesso 'supercutoff-errors:Read' / subject
 *    'supercutoff-errors' assegnato esplicitamente): in piu' vede la tabella di dettaglio e puo'
 *    lanciare il Retry. Il flag arriva via prop `canHandleErrors` calcolato nel container.
 *
 * Props:
 *   errors           {Array}     Lista errori [{ id, budget_id, project_code, error_message, error_type, retry_count, status, created_at }]
 *   retryResult      {object}    [opzionale] Risultato retry { retried, results: [] }
 *   onLoadErrors     {Function}  Callback per caricare gli errori
 *   onRetry          {Function}  Callback per il retry. Firma: fn({ error_ids: [] })
 *   onComplete       {Function}  Callback per completare lo step e avanzare al successivo
 *   loadingErrors    {boolean}   [opzionale] Default: false
 *   loadingRetry     {boolean}   [opzionale] Default: false
 *   canHandleErrors  {boolean}   [opzionale] True se l'utente ha il permesso AEP per vedere
 *                                il dettaglio errori e lanciare il retry. Default: false (fail-closed).
 */
const StepErrors = ({
  errors,
  retryResult,
  onLoadErrors,
  onRetry,
  onComplete,
  loadingErrors = false,
  loadingRetry = false,
  canHandleErrors = false,
}) => {
  const [selectedIds, setSelectedIds] = useState([])
  // Contatore retry: garantisce il reset della selezione anche se retryResult non cambia per riferimento
  const [retryCount, setRetryCount] = useState(0)

  useEffect(function() {
    if (retryCount > 0) {
      setSelectedIds([])
    }
  }, [retryCount])

  const safeErrors = errors || []

  // retriableErrors: errori ancora ritentabili. Include sia pending che retried perche'
  // il BE lascia lo status a `retried` finche' il job di retry non risolve l'errore — se
  // anche il retry fallisce, lo status resta `retried` con retry_count incrementato e
  // l'operatore deve poterlo ritentare di nuovo. Solo `resolved` e' uno stato terminale
  // che esclude la riga dalla selezione.
  const { retriableErrors, pendingCount, retriedCount, resolvedCount } = useMemo(() => ({
    retriableErrors: safeErrors.filter(function(e) {
      return e.status === STATUS_PENDING || e.status === STATUS_RETRIED
    }),
    pendingCount: safeErrors.filter(function(e) { return e.status === STATUS_PENDING }).length,
    retriedCount: safeErrors.filter(function(e) { return e.status === STATUS_RETRIED }).length,
    resolvedCount: safeErrors.filter(function(e) { return e.status === STATUS_RESOLVED }).length,
  }), [safeErrors])

  const allSelected = retriableErrors.length > 0 && selectedIds.length === retriableErrors.length

  // Riceve l'id dal secondo argomento di onChange (data.value) per evitare closure inline per riga
  const handleToggle = useCallback(function(id) {
    setSelectedIds(function(prev) {
      return prev.includes(id) ? prev.filter(function(i) { return i !== id }) : prev.concat([id])
    })
  }, [])

  const handleToggleAll = useCallback(function() {
    setSelectedIds(allSelected ? [] : retriableErrors.map(function(e) { return e.id }))
  }, [allSelected, retriableErrors])

  const handleRetry = useCallback(function() {
    // Guard esplicita: non procedere se loading o nessuna selezione
    if (loadingRetry || selectedIds.length === 0) return
    onRetry({ error_ids: selectedIds })
    setRetryCount(function(c) { return c + 1 })
    // Il reset di selectedIds avviene nell'useEffect su retryCount
  }, [loadingRetry, selectedIds, onRetry])

  // Permesso AEP per gestire (vedere dettaglio + retry) gli errori. Il flag arriva via prop
  // dal container, calcolato con ability.can('supercutoff-errors:Read', 'supercutoff-errors').
  // Coerente col binding a DB su POST /supercutoff/errors/retry: chi non lo ha riceverebbe
  // comunque 403 dal BE.

  // Lo step e' completabile quando la lista e' stata caricata (errors e' un array, non
  // null/undefined) e non ci sono piu' errori non terminali — ovvero ogni errore e'
  // 'resolved' oppure la lista e' vuota. Il BE non rimuove gli errori risolti dalla
  // response GET /supercutoff/errors, quindi non possiamo usare errors.length === 0 come
  // criterio: bloccherebbe l'avanzamento anche dopo che tutti gli errori sono stati
  // risolti correttamente. `every` su array vuoto restituisce true, quindi copre anche
  // il caso "nessun errore generato" senza bisogno di un check separato.
  // Il check Array.isArray copre entrambi i casi di "non caricato" (errors === null
  // dallo stato iniziale del reducer Redux, errors === undefined quando la prop e'
  // omessa nei test): in entrambi i casi canProceed resta false e al primo render
  // l'utente vede solo "Load errors", non "Proceed to next step".
  const canProceed =
    Array.isArray(errors) &&
    safeErrors.every(function(e) { return e.status === STATUS_RESOLVED })

  return (
    <div>
      <p className="supercutoff-step-description">
        Review budgets that failed during the closure process. Select the errors to retry
        and re-send them to the processing queue.
      </p>

      {/* Nessun errore */}
      {errors && safeErrors.length === 0 && (
        <Message positive icon>
          <Icon name="check circle" aria-hidden="true" />
          <Message.Content>
            <Message.Header>No errors found</Message.Header>
            All budgets were processed successfully. You can proceed to the final step.
          </Message.Content>
        </Message>
      )}

      {/* Tabella errori — solo per chi ha il permesso AEP. Senza permesso si mostra l'alert sotto. */}
      {safeErrors.length > 0 && canHandleErrors && (
        <>
          <div className="supercutoff-preview-box supercutoff-preview-box-mb">
            <h4>Errors summary</h4>
            <div className="supercutoff-preview-stats">
              <div className="supercutoff-stat">
                <span className="supercutoff-stat-value supercutoff-stat-value-error">
                  {pendingCount}
                </span>
                <span className="supercutoff-stat-label">Pending errors</span>
              </div>
              <div className="supercutoff-stat">
                <span className="supercutoff-stat-value supercutoff-stat-value-warning">
                  {retriedCount}
                </span>
                <span className="supercutoff-stat-label">Retried</span>
              </div>
              <div className="supercutoff-stat">
                <span className="supercutoff-stat-value supercutoff-stat-value-success">
                  {resolvedCount}
                </span>
                <span className="supercutoff-stat-label">Resolved</span>
              </div>
            </div>
          </div>

          <div className="supercutoff-cr-table-wrapper" aria-live="polite">
            <Table celled compact="very" size="small" aria-label="Errors list">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell collapsing>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedIds.length > 0 && !allSelected}
                      onChange={handleToggleAll}
                      aria-label="Select all retriable errors"
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell>Budget ID</Table.HeaderCell>
                  <Table.HeaderCell>Project Code</Table.HeaderCell>
                  <Table.HeaderCell>Error Type</Table.HeaderCell>
                  <Table.HeaderCell>Error Message</Table.HeaderCell>
                  <Table.HeaderCell>Retries</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {safeErrors.map(function(err) {
                  return (
                    <Table.Row key={err.id} negative={err.status === STATUS_PENDING}>
                      <Table.Cell collapsing>
                        <Checkbox
                          checked={selectedIds.includes(err.id)}
                          value={err.id}
                          onChange={function(_, data) { handleToggle(data.value) }}
                          disabled={err.status === STATUS_RESOLVED}
                          aria-label={'Select error ' + err.budget_id}
                        />
                      </Table.Cell>
                      <Table.Cell>{err.budget_id}</Table.Cell>
                      <Table.Cell>{err.project_code}</Table.Cell>
                      <Table.Cell>
                        <code
                          className="supercutoff-error-type"
                          aria-label={'Error type: ' + err.error_type}
                        >
                          {err.error_type}
                        </code>
                      </Table.Cell>
                      <Table.Cell className="supercutoff-error-message-cell">
                        <span className="supercutoff-error-message-text">{err.error_message}</span>
                      </Table.Cell>
                      <Table.Cell collapsing>{err.retry_count}</Table.Cell>
                      <Table.Cell collapsing>
                        <Label color={statusColor(err.status)} size="tiny">
                          {err.status}
                        </Label>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>
          </div>
        </>
      )}

      {/* Alert per gli utenti senza permesso AEP — mostrato solo quando ci sono errori */}
      {safeErrors.length > 0 && !canHandleErrors && (
        <Message warning icon aria-live="polite">
          <Icon name="warning sign" aria-hidden="true" />
          <Message.Content>
            <Message.Header>Errors require AEP team review</Message.Header>
            {NO_PERMISSION_MESSAGE}
          </Message.Content>
        </Message>
      )}

      {/* Risultato retry — visibile solo a chi ha permesso AEP (chi non lo ha non puo' aver lanciato il retry) */}
      {retryResult && canHandleErrors && (
        <Message positive icon className="supercutoff-retry-result" aria-live="polite">
          <Icon name="check circle" aria-hidden="true" />
          <Message.Content>
            <Message.Header>{retryResult.retried} error(s) re-queued for processing</Message.Header>
            Refresh the progress monitor (Step 8) to track the retry outcome.
          </Message.Content>
        </Message>
      )}

      {/* Area azioni — aria-live per annunciare i cambiamenti agli screen reader */}
      <div className="supercutoff-step-actions" aria-live="polite">
        {/* Bottone sempre visibile: prima del caricamento agisce come "Load errors", dopo
            il primo fetch diventa un refresh per ricaricare lo stato dal BE (utile dopo
            un retry per vedere se gli errori sono passati a `resolved`). Stile allineato
            al bottone Refresh di StepProgress: niente colore primary (default Semantic
            UI grey), size small, aria-busy durante il loading. */}
        <Button
          size="small"
          icon
          labelPosition="left"
          onClick={onLoadErrors}
          loading={loadingErrors}
          disabled={loadingErrors}
          aria-busy={loadingErrors}
        >
          <Icon name={Array.isArray(errors) ? 'refresh' : 'search'} aria-hidden="true" />
          {Array.isArray(errors) ? 'Refresh errors' : 'Load errors'}
        </Button>

        {safeErrors.length > 0 && retriableErrors.length > 0 && canHandleErrors && (
          <Button
            color="orange"
            icon
            labelPosition="left"
            onClick={handleRetry}
            loading={loadingRetry}
            disabled={loadingRetry || selectedIds.length === 0}
          >
            <Icon name="redo" aria-hidden="true" />
            Retry selected ({selectedIds.length})
          </Button>
        )}

        {canProceed && (
          <Button
            positive
            icon
            labelPosition="left"
            onClick={onComplete}
          >
            <Icon name="arrow right" aria-hidden="true" />
            Proceed to next step
          </Button>
        )}
      </div>
    </div>
  )
}

StepErrors.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    budget_id: PropTypes.number,
    project_code: PropTypes.string,
    error_message: PropTypes.string,
    error_type: PropTypes.string,
    retry_count: PropTypes.number,
    status: PropTypes.string,
  })),
  retryResult: PropTypes.shape({
    retried: PropTypes.number,
    results: PropTypes.array,
  }),
  onLoadErrors: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  loadingErrors: PropTypes.bool,
  loadingRetry: PropTypes.bool,
  canHandleErrors: PropTypes.bool,
}

export default StepErrors
